import { Injectable } from '@angular/core';
import { CanActivate } from '@angular/router';
import { EventId } from '@interfaces';
import { FlowTypes, RejectedAccount, urlMapper } from '@interfaces/models';
import { select, Store } from '@ngrx/store';
import { LoggerCodes } from '@shared/modules/logger/logger';
import {
  closeSpinner,
  Go,
  LogError,
  LogInfo,
  State,
  TrackProcessError,
  UpdateDisplayedAccounts,
} from '@store/index';
import {
  selectClientAccountsLoaded,
  selectEligibleInternalTransferFromAccountsWithRejects,
  selectEligibleInternalTransferToAccountsWithRejects,
} from '@store/selectors/client-accounts/client-accounts.selectors';
import { selectInternalTransferEligibility } from '@store/selectors/eligibility/internal-transfer/internal-transfer.eligibility.selectors';
import { CookieService } from 'ngx-cookie-service';
import { catchError, filter, map, Observable, of, withLatestFrom } from 'rxjs';

@Injectable({ providedIn: 'root' })
export class InternalTransferEligibilityGuard implements CanActivate {
  rejectedFromAccounts: RejectedAccount[] = [];
  rejectedToAccounts: RejectedAccount[] = [];
  constructor(
    private globalStore: Store<State>,
    private cookieService: CookieService,
  ) {}
  canActivate(): Observable<boolean> {
    if (this.cookieService.get('routeGuard') === 'OFF') {
      return of(true);
    }
    return this.globalStore.pipe(
      // Ensure that the client accounts are loaded before proceeding with eligibility check
      select(selectClientAccountsLoaded),
      filter((loaded) => loaded),
      // Check the client's eligibility with data from the store, and return that along with the from accounts
      withLatestFrom(
        this.globalStore.select(selectInternalTransferEligibility),
        this.globalStore.select(
          selectEligibleInternalTransferToAccountsWithRejects(this.rejectedToAccounts),
        ),
        this.globalStore.select(
          selectEligibleInternalTransferFromAccountsWithRejects(this.rejectedFromAccounts),
        ),
      ),
      map(([, payload]) => {
        // If the client is eligible, update display accounts + log + close loading spinner and then activate page
        if (payload.isInternalTransferEligible) {
          if (!payload.fromAccounts) {
            throw new Error('BAD_PAYLOAD_FROM_ACCOUNTS');
          }
          this.globalStore.dispatch(
            UpdateDisplayedAccounts({
              payload: payload.fromAccounts,
            }),
          );
          this.globalStore.dispatch(
            LogInfo({
              payload: {
                eventId: 'internal-transfer-guard--eligible-accounts-found',
                message: `Internal Transfer Eligibility Guard eligibility success ${FlowTypes.INTERNAL_TRANSFER} ${payload.accountFromType} account`,
                eligibleFromType: payload.accountFromType,
                code: LoggerCodes.GENERAL_SUCCESS,
              },
            }),
          );
          this.globalStore.dispatch(closeSpinner());
          // If the client is ineligible, go to the ineligible page along with logging/tracking error
        } else {
          this.globalStore.dispatch(
            Go({ path: [`${urlMapper[FlowTypes.INTERNAL_TRANSFER]}/ineligible`] }),
          );

          let message = 'No eligible internal transfer accounts found';

          switch (true) {
            case !!payload.ineligibleFromAccountType && !!payload.ineligibleToAccountType:
              this.globalStore.dispatch(
                TrackProcessError({
                  errorCode: `coo-it-ineligible-both-${payload.ineligibleFromAccountType}-${payload.ineligibleToAccountType}_account`,
                }),
              );
              message = `No eligible IT ${payload.ineligibleToAccountType} TO account nor ${payload.ineligibleFromAccountType} FROM account`;
              break;
            case !!payload.ineligibleFromAccountType:
              this.globalStore.dispatch(
                TrackProcessError({
                  errorCode: `coo-it-ineligible-from-${payload.ineligibleFromAccountType}_account`,
                }),
              );
              message = `No eligible IT ${payload.ineligibleFromAccountType} FROM account`;
              break;
            case !!payload.ineligibleToAccountType:
              this.globalStore.dispatch(
                TrackProcessError({
                  errorCode: `coo-it-ineligible-to-${payload.ineligibleToAccountType}_account`,
                }),
              );
              message = `No eligible IT ${payload.ineligibleToAccountType} TO account`;
              break;
          }

          this.globalStore.dispatch(
            LogError({
              payload: {
                eventId: EventId.INELIGIBLE_PAGE_ROUTE,
                message,
                ineligibleFromAccountType: payload.ineligibleFromAccountType,
                ineligibleToAccountType: payload.ineligibleToAccountType,
                flowType: FlowTypes.INTERNAL_TRANSFER,
                rejectedFromAccounts: this.rejectedFromAccounts,
                rejectedToAccounts: this.rejectedToAccounts,
              },
            }),
          );
        }

        return payload.isInternalTransferEligible;
      }),
      catchError((err) => {
        this.globalStore.dispatch(Go({ path: ['error'] }));
        this.globalStore.dispatch(
          LogError({
            payload: {
              eventId: 'internal-transfer-guard--unknown-error',
              message: `Internal Transfer Eligibility Guard failed due to unknown error. ${err.message}`,
              code: LoggerCodes.NULL_FAILURE,
            },
          }),
        );
        this.globalStore.dispatch(
          TrackProcessError({
            errorCode: `coo-it-ineligible-app_error`,
          }),
        );

        return of(false);
      }),
    );
  }
}
